// useScreenSize.js
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { blockingWait } from "./utils";

export const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleResize = useCallback(() => {
    setScreenSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return screenSize;
};

export const useDoubleClick = (onClick, onDbClick, delay = 300) => {
  const timePassed = useRef(0);
  let timeoutID;
  return (e) => {
    if (e.detail === 1) {
      timeoutID = setTimeout(() => {
        if (Date.now() - timePassed.current >= delay) {
          onClick();
        }
      }, delay);
    }

    if (e.detail === 2) {
      clearTimeout(timeoutID);
      timePassed.current = Date.now();
      onDbClick(e);
    }
  };
};

function debounce(fn, ms) {
  let timer;
  return (_) => {
    clearTimeout(timer);
    timer = setTimeout((_) => {
      timer = null;
      fn.apply(this, arguments);
    }, ms);
  };
}

export const useDimensions = (targetRef) => {
  const getDimensions = () => {
    return {
      width: targetRef.current ? targetRef.current.offsetWidth : 0,
      height: targetRef.current ? targetRef.current.offsetHeight : 0,
    };
  };

  const [dimensions, setDimensions] = useState(getDimensions);

  const handleResize = () => {
    setDimensions(getDimensions());
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useLayoutEffect(() => {
    handleResize();
  }, []);

  return dimensions;
};

export const useTime = (counting, ms = 1000) => {
  const [seconds, setSeconds] = useState(0);
  const timeout = useRef();

  useEffect(() => {
    increaseSeconds(ms);
  }, [seconds]);

  useEffect(() => {
    if (!counting) {
      clearTimeout(timeout.current);
    } else {
      setSeconds(0);
    }
    return () => clearTimeout(timeout.current);
  }, [counting]);

  function increaseSeconds(ms) {
    if (document.visibilityState === "visible") {
      timeout.current = setTimeout(() => {
        setSeconds((seconds) => seconds + 1);
      }, ms);
    } else {
      blockingWait(ms);
      setSeconds((seconds) => seconds + 1);
    }
  }

  return [seconds, setSeconds];
};
